import { motion } from 'framer-motion';
import { TrendingUp, Users, Clock, Gift, CheckCircle } from 'lucide-react';

export default function CrowdfundPage() {
  // Simplified version without wagmi to avoid compatibility issues
  // Mock data (replace with WebSocket or contract read for live crowdfund)
  const mockData = {
    totalRaised: '10.5',
    minGoal: '5',
    maxGoal: '20',
    daysLeft: 12,
    contributors: 127,
  };

  const progressToMax = (parseFloat(mockData.totalRaised) / parseFloat(mockData.maxGoal)) * 100;
  const progress = Math.min(progressToMax, 100);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Title + current phase */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="text-center mb-8 md:mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-3 md:mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            Liquidity Crowdfund
          </h1>
          <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto mb-4">
            DejaPortal 100% Fair Launch — No presale, no VC, no team pre-mining.
            Contribute ETH to receive OG Portal NFT, community airdrop, and endgame rewards.
          </p>
          <p className="text-sm md:text-base text-purple-300/90 max-w-2xl mx-auto">
            Current phase: Grants application + community crowdfund for LP. Target $25–75k initial liquidity.
          </p>
        </motion.div>

        {/* Progress Card — Live crowdfund (ETH raised / goal) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8 mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Crowdfund Progress</h2>
            <span className="px-3 py-1 bg-green-500/20 border border-green-500/50 rounded-full text-xs text-green-400 font-medium">Live</span>
          </div>
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>ETH Raised</span>
              <span>Goal: {mockData.minGoal}–{mockData.maxGoal} ETH</span>
            </div>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            <div className="text-right mt-2">
              <span className="text-3xl font-bold text-purple-400">
                {mockData.totalRaised} ETH
              </span>
              <span className="text-gray-400 ml-2">/ {mockData.maxGoal} ETH ({progressToMax.toFixed(1)}%)</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: TrendingUp, label: 'Raised', value: mockData.totalRaised + ' ETH' },
              { icon: Users, label: 'Contributors', value: mockData.contributors },
              { icon: Clock, label: 'Days Left', value: mockData.daysLeft },
              { icon: Gift, label: 'OG NFTs', value: '200/200' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <stat.icon className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contribution Form */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Contribute ETH</h3>

            <button className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold text-lg">
              Connect Wallet to Contribute
            </button>

            <p className="text-sm text-gray-400 mt-4 text-center">
              Contract integration coming soon
            </p>
          </motion.div>

          {/* Right: Rewards */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-white mb-6">Contribution Rewards</h3>

            <div className="space-y-4">
              {[
                {
                  threshold: '0.1',
                  rewards: ['🎁 Free OG Portal NFT (worth 200 $DEJA)', '⭐ Reputation +50', '💎 1B $DEJA airdrop (pro-rata)', '🗳️ 2x voting power'],
                },
                {
                  threshold: '0.5',
                  rewards: ['📛 "Genesis Builder" badge NFT', '⚡ 3x voting power'],
                },
                {
                  threshold: '1.0',
                  rewards: ['👑 5x endgame rewards', '🏆 Free DAO proposals', '🌌 30% multiverse creation discount'],
                },
              ].map((tier) => (
                <div
                  key={tier.threshold}
                  className="p-4 rounded-lg border-2 border-slate-700 bg-slate-700/20"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-white">≥{tier.threshold} ETH</span>
                  </div>
                  <div className="space-y-1">
                    {tier.rewards.map((reward) => (
                      <div key={reward} className="text-sm text-gray-300">• {reward}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Rules */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-slate-800/30 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8"
        >
          <h3 className="text-2xl font-bold text-white mb-6">Crowdfund Rules</h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-300">
            <div>
              <h4 className="font-semibold text-purple-400 mb-2">✅ Fund Usage</h4>
              <ul className="space-y-1 text-sm">
                <li>• 100% for Aerodrome DEX initial liquidity</li>
                <li>• Pairing: WETH/$DEJA</li>
                <li>• LP tokens locked 12 months (Unicrypt)</li>
                <li>• Combined with Base Builder Grants (1-5 ETH)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-purple-400 mb-2">🔒 Transparency</h4>
              <ul className="space-y-1 text-sm">
                <li>• Crowdfund address publicly disclosed (Basescan)</li>
                <li>• Discord bot real-time contribution updates</li>
                <li>• LP addition livestreamed within 24h</li>
                <li>• Full refund if &lt;5 ETH minimum not met</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
